import { GlobalMessages } from '@shared/localization/messages';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useTenantQueryActions } from '@/api/hooks/tenantQueryActions';
import { Unauthorized } from '@/components/atoms/Unauthorized';
import { TabHeader } from '@/components/molecules/TabHeader';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { useHasGlobalPermission } from '@/hooks/useHasGlobalPermission';
import { AdministrationTabsWrapper } from '@/pages/administration/tabs/wrapper';
import { parseProblemDetails } from '@/utils/errors';
import { handleValidationErrors } from '@/utils/validationErrors';

import { ExportSettingsMessages } from './messages';
import { StyleSection } from './StyleSection';

interface ExportSettingsFormData {
  // Unified Styles (applied to both PDF and Excel)
  tableHeaderFontColor: string;
  tableHeaderBackgroundColor: string;
  tableHeaderFontFamily: string;
  tableHeaderBold: boolean;
  tableHeaderItalic: boolean;
  tableRowFontColor: string;
  tableRowBackgroundColor: string;
  tableRowFontFamily: string;
  tableRowBold: boolean;
  tableRowItalic: boolean;
  tableSummaryFontColor: string;
  tableSummaryBackgroundColor: string;
  tableSummaryFontFamily: string;
  tableSummaryBold: boolean;
  tableSummaryItalic: boolean;
}

const fontFamilyOptions = [
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Helvetica', label: 'Helvetica' },
];

const ExportSettingsTab = () => {
  const messages = useMessages(ExportSettingsMessages);
  const globalMessages = useMessages(GlobalMessages);
  const toast = useToast();
  const hasPermission = useHasGlobalPermission('CAN_MANAGE_TENANT_SETTINGS');

  const { useGetTenantExportConfig, useUpdateTenantExportConfig } = useTenantQueryActions();
  // Gate the fetch, not just the render (EWM-950).
  const { data: config, isLoading: isLoadingConfig } = useGetTenantExportConfig({
    enabled: hasPermission === true,
  });
  const { mutateAsync: updateConfig, isPending } = useUpdateTenantExportConfig();

  const { control, handleSubmit, setValue, reset } = useForm<ExportSettingsFormData>({
    defaultValues: {
      tableHeaderFontColor: '',
      tableHeaderBackgroundColor: '',
      tableHeaderFontFamily: '',
      tableHeaderBold: false,
      tableHeaderItalic: false,
      tableRowFontColor: '',
      tableRowBackgroundColor: '',
      tableRowFontFamily: '',
      tableRowBold: false,
      tableRowItalic: false,
      tableSummaryFontColor: '',
      tableSummaryBackgroundColor: '',
      tableSummaryFontFamily: '',
      tableSummaryBold: false,
      tableSummaryItalic: false,
    },
  });

  // Watch all form values at once for better performance
  const watchedValues = useWatch({ control });

  // Load config from API into form
  useEffect(() => {
    if (config && !isLoadingConfig) {
      reset(
        {
          tableHeaderFontColor: config.tableHeaderFontColor ?? '',
          tableHeaderBackgroundColor: config.tableHeaderBackgroundColor ?? '',
          tableHeaderFontFamily: config.tableHeaderFontFamily ?? '',
          tableHeaderBold: config.tableHeaderBold ?? false,
          tableHeaderItalic: config.tableHeaderItalic ?? false,
          tableRowFontColor: config.tableRowFontColor ?? '',
          tableRowBackgroundColor: config.tableRowBackgroundColor ?? '',
          tableRowFontFamily: config.tableRowFontFamily ?? '',
          tableRowBold: config.tableRowBold ?? false,
          tableRowItalic: config.tableRowItalic ?? false,
          tableSummaryFontColor: config.tableSummaryFontColor ?? '',
          tableSummaryBackgroundColor: config.tableSummaryBackgroundColor ?? '',
          tableSummaryFontFamily: config.tableSummaryFontFamily ?? '',
          tableSummaryBold: config.tableSummaryBold ?? false,
          tableSummaryItalic: config.tableSummaryItalic ?? false,
        },
        { keepDefaultValues: false },
      );
    }
  }, [config, isLoadingConfig, reset]);

  const onSubmit = async (data: ExportSettingsFormData) => {
    try {
      await updateConfig({
        tableHeaderFontColor: data.tableHeaderFontColor || null,
        tableHeaderBackgroundColor: data.tableHeaderBackgroundColor || null,
        tableHeaderFontFamily: data.tableHeaderFontFamily || null,
        tableHeaderBold: data.tableHeaderBold ?? null,
        tableHeaderItalic: data.tableHeaderItalic ?? null,
        tableRowFontColor: data.tableRowFontColor || null,
        tableRowBackgroundColor: data.tableRowBackgroundColor || null,
        tableRowFontFamily: data.tableRowFontFamily || null,
        tableRowBold: data.tableRowBold ?? null,
        tableRowItalic: data.tableRowItalic ?? null,
        tableSummaryFontColor: data.tableSummaryFontColor || null,
        tableSummaryBackgroundColor: data.tableSummaryBackgroundColor || null,
        tableSummaryFontFamily: data.tableSummaryFontFamily || null,
        tableSummaryBold: data.tableSummaryBold ?? null,
        tableSummaryItalic: data.tableSummaryItalic ?? null,
      });

      toast.showToast(messages.apiSaveDataSuccess(), 'success');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const problemDetails = parseProblemDetails(error);
      handleValidationErrors(problemDetails, setValue, toast);
      toast.showToast(messages.apiSaveDataError(), 'error');
    }
  };

  if (!hasPermission) {
    return <Unauthorized />;
  }

  const renderStyleSection = () => {
    const getValue = (field: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return watchedValues[field as keyof ExportSettingsFormData] as any;
    };
    const setValueForField = (field: string, value: string | boolean) => {
      setValue(field as keyof ExportSettingsFormData, value);
    };

    return (
      <div className='admin-block-item mb-6'>
        <h6 className='mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100'>
          {messages.exportSettingsTitle()}
        </h6>
        <p className='mb-4 text-sm text-gray-600 dark:text-gray-400'>
          {messages.exportSettingsDescription()}
        </p>

        <StyleSection
          title={messages.tableHeadersLabel()}
          fieldPrefix='tableHeader'
          getValue={getValue}
          setValueForField={setValueForField}
          messages={messages}
          fontFamilyOptions={fontFamilyOptions}
        />

        <StyleSection
          title={messages.tableRowsLabel()}
          fieldPrefix='tableRow'
          getValue={getValue}
          setValueForField={setValueForField}
          messages={messages}
          fontFamilyOptions={fontFamilyOptions}
        />

        <StyleSection
          title={messages.tableSummaryLabel()}
          fieldPrefix='tableSummary'
          getValue={getValue}
          setValueForField={setValueForField}
          messages={messages}
          fontFamilyOptions={fontFamilyOptions}
        />
      </div>
    );
  };

  return (
    <AdministrationTabsWrapper>
      <TabHeader
        title={messages.header()}
        onSave={handleSubmit(onSubmit)}
        saveButtonProcessing={isPending}
      />
      {isLoadingConfig ? (
        <div>{globalMessages.loading()}</div>
      ) : (
        <div className='admin-block'>{renderStyleSection()}</div>
      )}
    </AdministrationTabsWrapper>
  );
};

export default ExportSettingsTab;
